import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { IconButton } from './Button'
import { ConfirmProvider } from './Confirm'

export interface SnackbarOptions {
  message: string
  /** Il tono non cambia l'aspetto (M3: niente icone ne' stili aggiuntivi): `error` rende l'annuncio assertivo. */
  tone?: 'neutral' | 'success' | 'error' | undefined
  actionLabel?: string | undefined
  onAction?: (() => void) | undefined
  /** Millisecondi prima della chiusura automatica (default 4000). */
  duration?: number | undefined
}

interface QueueItem extends SnackbarOptions {
  key: number
}

interface SnackbarApi {
  show: (options: SnackbarOptions) => void
}

const SnackbarContext = createContext<SnackbarApi | null>(null)

/** Sostituisce `alert()`. Una sola snackbar alla volta, le altre vanno in coda FIFO. */
export function useSnackbar(): SnackbarApi {
  const ctx = useContext(SnackbarContext)
  if (!ctx) throw new Error('useSnackbar richiede <SnackbarProvider>')
  return ctx
}

export function SnackbarProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<QueueItem[]>([])
  const counter = useRef(0)
  const hostRef = useRef<HTMLDivElement | null>(null)
  const [shownKey, setShownKey] = useState<number | null>(null)

  const show = useCallback((options: SnackbarOptions) => {
    counter.current += 1
    const item: QueueItem = { ...options, key: counter.current }
    setQueue((q) => [...q, item])
  }, [])

  const dismiss = useCallback((key: number) => setQueue((q) => q.filter((i) => i.key !== key)), [])

  const api = useMemo<SnackbarApi>(() => ({ show }), [show])
  const current = queue[0]

  useEffect(() => {
    if (!current) return undefined
    const t = window.setTimeout(() => dismiss(current.key), current.duration ?? 4000)
    return () => window.clearTimeout(t)
  }, [current, dismiss])

  // Top layer: a ogni nuovo messaggio il popover viene riaperto (finisce sopra un eventuale dialog
  // modale), poi il contenuto compare dopo un attimo cosi' le regioni live lo annunciano.
  const currentKey = current?.key
  useEffect(() => {
    const host = hostRef.current as (HTMLDivElement & { showPopover?: () => void; hidePopover?: () => void }) | null
    if (currentKey === undefined) {
      setShownKey(null)
      try { host?.hidePopover?.() } catch { /* non aperto */ }
      return undefined
    }
    try {
      host?.hidePopover?.()
      host?.showPopover?.()
    } catch { /* senza supporto resta position:fixed */ }
    const t = window.setTimeout(() => setShownKey(currentKey), 50)
    return () => window.clearTimeout(t)
  }, [currentKey])

  const tone = current?.tone ?? 'neutral'
  const node = current && shownKey === current.key ? (
    <div key={current.key} className="md-snackbar">
      <span className="md-snackbar__message">{current.message}</span>
      {current.actionLabel ? (
        <button
          type="button"
          className="md-snackbar__action md-state"
          onClick={() => {
            current.onAction?.()
            dismiss(current.key)
          }}
        >
          {current.actionLabel}
        </button>
      ) : null}
      <IconButton
        icon="close"
        size="sm"
        aria-label="Chiudi notifica"
        className="md-snackbar__close"
        tooltip={false}
        onClick={() => dismiss(current.key)}
      />
    </div>
  ) : null

  return (
    <SnackbarContext.Provider value={api}>
      <ConfirmProvider>{children}</ConfirmProvider>
      <div className="md-snackbar-host" ref={hostRef} {...{ popover: 'manual' }}>
        {/* Due regioni sempre montate: gli annunci cambiano solo se cambia il contenuto. */}
        <div aria-live="polite" role="status">
          {tone !== 'error' ? node : null}
        </div>
        <div aria-live="assertive" role="alert">
          {tone === 'error' ? node : null}
        </div>
      </div>
    </SnackbarContext.Provider>
  )
}
